<template>
  <div
    data-testid="iconcomponent"
    class="icon-renderer ui-flex ui-justify-center ui-items-center"
    :class="svgClass"
    :style="{ width: `${width}px`, height: `${height}px` }"
    v-html="processedSvgContent"
  />
</template>

<script lang="ts" setup>
import type { SvgIconProps } from "./types";
import { computed, ref, watch } from "vue";
import { toRefs } from "vue";
import { smartIconService } from "../../services/SmartIconService";
import * as iconManifest from "../../assets/icon-manifest.json";

const props = defineProps<SvgIconProps>();
const { name, width, height, svgClass, color } = toRefs(props);

const computedWidth = computed(() => `${width.value}px`);
const computedHeight = computed(() => `${height.value}px`);
const computedColor = computed(() => color?.value || "none");

const svgContent = ref<string>("");

const processedSvgContent = computed(() => {
  if (!svgContent.value) return "";

  // Apply size and color to the SVG
  return svgContent.value.replace(
    "<svg",
    `<svg width="${width.value}" height="${height.value}" fill="${computedColor.value}"`,
  );
});

const loadIcon = async (iconName: string) => {
  if (!iconManifest.icons.includes(iconName)) {
    svgContent.value = "";
    return;
  }

  try {
    // Check if it's a critical icon first (synchronous)
    if (smartIconService.isCritical(iconName)) {
      const content = smartIconService.getCriticalIconSync(iconName);
      svgContent.value = content || "";
      return;
    }

    // For non-critical icons, use async loading
    const content = await smartIconService.getIcon(iconName);
    svgContent.value = content;
  } catch (error) {
    console.error(`Error loading icon ${iconName}:`, error);
    svgContent.value = "";
  }
};

// Load icon when name changes
watch(
  name,
  (newName) => {
    if (newName) {
      loadIcon(newName);
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.icon-renderer {
  width: v-bind(computedWidth);
  height: v-bind(computedHeight);
  color: v-bind(computedColor);
}
</style>
